import { ref, Ref } from 'vue';
import { Annotation, Counterpoint } from '../types/annotation.types';
import { EventBus } from '@/assets/js/event-bus';

export function useAnnotationEditing(
  annotation: Ref<Annotation>,
  parentCounterpoint: Ref<Counterpoint>,
  updateAnnotationText: (params: any) => Promise<void>,
  updateTeamReply: (params: any) => Promise<void>,
  switchAnnotationType: (params: any) => Promise<void>,
  annotationFocusIn: (annotation: Annotation) => void,
  annotationFocusOut: () => void,
  getRefByAnnotationsIds: (annotation: Annotation) => string[]
) {
  const annotationText = ref<string>('');
  const teamReplyComment = ref<string>('');
  const previousText = ref<string>('');
  const saving = ref<boolean>(false);
  const errorButton = ref<boolean>(false);
  const editMode = ref<boolean>(false);
  const activeFocus = ref<boolean>(false);

  const doFocusIn = (useInstruction: boolean) => {
    const refAnnotations = getRefByAnnotationsIds(annotation.value);
    EventBus.emit('annotation.active', refAnnotations[refAnnotations.length - 1]);
    annotationFocusIn(annotation.value);
    activeFocus.value = true;
    previousText.value = useInstruction
      ? annotation.value.instruction || ''
      : annotation.value.comment;
  };

  const doFocusOut = async (
    showCounterpointSelect: boolean,
    isHostInstructionMode: boolean,
    counterpointData: any
  ) => {
    if (showCounterpointSelect) return;

    EventBus.emit('annotation.active', '');
    annotationFocusOut();
    activeFocus.value = false;
    saving.value = true;

    // Disable pointer events on ref annotation during save
    if (annotation.value?.refAnnotation?._id) {
      const annotationId = annotation.value.refAnnotation._id;
      const annotationElement = document.querySelector(`#annotation-${annotationId}`);
      if (annotationElement) {
        (annotationElement as HTMLElement).style.pointerEvents = 'none';
      }
    }

    try {
      await updateAnnotationText({
        annotation: annotation.value,
        newText: annotationText.value.replace(/&nbsp;/g, ' '),
        oldText: previousText.value,
        editedByTeamLead: editMode.value,
      });
    } catch (err) {
      console.error(err);
      errorButton.value = true;
    }

    // Update host thread if needed
    if (isHostInstructionMode) {
      const refByAnnotation = counterpointData?.refByAnnotationsMap?.[annotation.value._id];
      if (refByAnnotation && refByAnnotation.length) {
        EventBus.emit(
          'annotation.host.updated',
          refByAnnotation[0],
          previousText.value,
          annotationText.value
        );
      }
    }

    saving.value = false;

    // Re-enable pointer events
    if (annotation.value?.refAnnotation?._id) {
      const annotationId = annotation.value.refAnnotation._id;
      const annotationElement = document.querySelector(`#annotation-${annotationId}`);
      if (annotationElement) {
        (annotationElement as HTMLElement).style.pointerEvents = 'auto';
      }
    }

    editMode.value = false;
    return true;
  };

  const doTeamReplyFocusIn = () => {
    annotationFocusIn(annotation.value);
    previousText.value = teamReplyComment.value || annotation.value.comment;
  };

  const doTeamReplyFocusOut = async () => {
    annotationFocusOut();
    saving.value = true;

    try {
      await updateTeamReply({
        annotation: annotation.value,
        newText: teamReplyComment.value,
        oldText: previousText.value,
      });
    } catch (error) {
      console.error(error);
      errorButton.value = true;
    }

    saving.value = false;
    return true;
  };

  const handleAnnotationSwitch = async () => {
    saving.value = true;
    const dataToUpdate: any = {};

    if (annotation.value.type === 'instruction') {
      dataToUpdate.comment = annotation.value.instruction;
      dataToUpdate.type = 'comment';
    } else {
      dataToUpdate.instruction = annotation.value.comment;
      dataToUpdate.comment = ' ';
      dataToUpdate.type = 'instruction';
    }

    await switchAnnotationType({
      annotation: annotation.value,
      dataToUpdate,
      parentCounterpoint: parentCounterpoint.value,
    });

    saving.value = false;
    return true;
  };

  const doRestoreText = () => {
    annotationText.value = annotation.value.oldComment || '';
  };

  const setAnnotationSaving = (status: string) => {
    saving.value = status === 'Saving';
    errorButton.value = status === 'Error';
  };

  return {
    annotationText,
    teamReplyComment,
    previousText,
    saving,
    errorButton,
    editMode,
    activeFocus,
    doFocusIn,
    doFocusOut,
    doTeamReplyFocusIn,
    doTeamReplyFocusOut,
    handleAnnotationSwitch,
    doRestoreText,
    setAnnotationSaving,
  };
}
